import { useState, useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  Layers,
  BookOpen,
  FolderOpen,
  FileText,
  Video,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { contentApi, type ContentHierarchyNode, type ContentHierarchyResponse } from '@/api/content'
import type { LearnerClassCourse } from '@/api/b2b'
import { cn } from '@/lib/utils'

interface ReviewContentSidebarProps {
  classCourses: LearnerClassCourse[]
  currentLessonId?: number
  onLessonSelect?: (lessonId: number, contentId: number) => void
  includeInactive?: boolean
  className?: string
}

interface ContentEntry {
  contentId: number
  label: string
}

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'curriculum':
      return Layers
    case 'course':
      return BookOpen
    case 'module':
      return FolderOpen
    case 'lesson':
      return Video
    default:
      return FileText
  }
}

export function ReviewContentSidebar({
  classCourses,
  currentLessonId,
  onLessonSelect,
  includeInactive = false,
  className,
}: ReviewContentSidebarProps) {
  const { t } = useTranslation('learner')

  // All top-level sections expanded by default
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({})
  const [openNodes, setOpenNodes] = useState<Record<string, boolean>>({})

  // Extract all unique contentIds from classCourses
  const contentEntries = useMemo((): ContentEntry[] => {
    const seen = new Set<number>()
    const entries: ContentEntry[] = []

    for (const course of classCourses) {
      if (course.isBundle && course.bundleContents) {
        for (const bc of course.bundleContents) {
          if (bc.contentId && !seen.has(bc.contentId)) {
            seen.add(bc.contentId)
            entries.push({
              contentId: bc.contentId,
              label: bc.contentTitle || `Content ${bc.contentId}`,
            })
          }
        }
      } else if (course.contentId && !seen.has(course.contentId)) {
        seen.add(course.contentId)
        entries.push({
          contentId: course.contentId,
          label: course.productName || course.contentTitle || `Content ${course.contentId}`,
        })
      }
    }

    return entries
  }, [classCourses])

  // Fetch hierarchy for each contentId
  const hierarchyQueries = useQueries({
    queries: contentEntries.map((entry) => ({
      queryKey: ['contentHierarchy', entry.contentId, { includeInactive: includeInactive }],
      queryFn: () => contentApi.getContentHierarchy(entry.contentId, includeInactive),
      enabled: !!entry.contentId,
      staleTime: 10 * 60 * 1000,
    })),
  })

  // Initialize sections as expanded by default
  const isSectionOpen = (contentId: number) => {
    return openSections[contentId] !== false // default true
  }

  const toggleSection = (contentId: number) => {
    setOpenSections(prev => ({
      ...prev,
      [contentId]: prev[contentId] === false ? true : false,
    }))
  }

  // Node key includes contentId to avoid collisions across different hierarchies
  const getNodeKey = (contentId: number, nodeId: number) => `${contentId}-${nodeId}`

  const isNodeOpen = (contentId: number, nodeId: number) => {
    const key = getNodeKey(contentId, nodeId)
    return openNodes[key] !== false // default true (all expanded)
  }

  const toggleNode = (contentId: number, nodeId: number) => {
    const key = getNodeKey(contentId, nodeId)
    setOpenNodes(prev => ({
      ...prev,
      [key]: prev[key] === false ? true : false,
    }))
  }

  const renderNode = (
    node: ContentHierarchyNode,
    contentId: number,
    depth: number = 0
  ) => {
    const isLesson = node.type === 'lesson'
    const hasChildren = node.children.length > 0
    const isOpen = isNodeOpen(contentId, node.id)
    const isSelected = isLesson && node.id === currentLessonId
    const Icon = getTypeIcon(node.type)

    if (isLesson) {
      return (
        <button
          key={node.id}
          onClick={() => onLessonSelect?.(node.id, contentId)}
          className={cn(
            'w-full group flex items-start gap-3 px-4 py-2.5 transition-all relative text-left',
            {
              'bg-indigo-50 hover:bg-indigo-50 text-indigo-900': isSelected,
              'text-gray-600 hover:bg-gray-50': !isSelected,
            }
          )}
          style={{ paddingLeft: `${16 + depth * 16}px` }}
        >
          {isSelected && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-600" />
          )}
          <div
            className={cn('mt-0.5 flex-shrink-0', {
              'text-indigo-600': isSelected,
              'text-gray-400 group-hover:text-gray-600': !isSelected,
            })}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className={cn('text-sm transition-colors', {
                'font-bold text-indigo-700': isSelected,
                'font-medium group-hover:text-gray-900': !isSelected,
              })}
            >
              <ContentMetadataText text={node.bareTitle || node.title} />
            </div>
          </div>
        </button>
      )
    }

    // Container node (course, module)
    return (
      <div key={node.id}>
        <button
          onClick={() => toggleNode(contentId, node.id)}
          className={cn(
            'w-full flex items-center gap-2 px-4 py-2 text-left transition-colors',
            isOpen ? 'bg-gray-50' : 'hover:bg-gray-50'
          )}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          <div className="flex-shrink-0 text-gray-400">
            {isOpen ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </div>
          <div
            className={cn('flex-shrink-0', {
              'text-indigo-600': node.type === 'course',
              'text-amber-600': node.type === 'module',
              'text-purple-600': node.type === 'curriculum',
            })}
          >
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-sm font-semibold text-gray-800 truncate">
            <ContentMetadataText text={node.bareTitle || node.title} />
          </span>
        </button>
        {isOpen && hasChildren && (
          <div>
            {node.children
              .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
              .map(child => renderNode(child, contentId, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const renderHierarchy = (
    hierarchy: ContentHierarchyResponse,
    contentId: number
  ) => {
    if (!hierarchy.children || hierarchy.children.length === 0) {
      return (
        <p className="px-4 py-2 text-xs text-gray-400">{t('learning.reviewSidebar.noContent')}</p>
      )
    }

    return hierarchy.children
      .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
      .map(child => renderNode(child, contentId, 1))
  }

  const isAllLoading = hierarchyQueries.length > 0 && hierarchyQueries.every(q => q.isLoading)

  if (contentEntries.length === 0 && !isAllLoading) {
    return (
      <div className={cn('w-80 bg-gray-50 border-r border-gray-200 h-full flex flex-col', className)}>
        <div className="px-4 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{t('learning.reviewSidebar.contentReview')}</h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-gray-500">{t('learning.reviewSidebar.noContent')}</p>
        </div>
      </div>
    )
  }

  if (isAllLoading) {
    return (
      <div className={cn('w-80 bg-gray-50 border-r border-gray-200 h-full flex flex-col', className)}>
        <div className="px-4 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{t('learning.reviewSidebar.contentReview')}</h2>
        </div>
        <div className="flex-1 p-4 space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="space-y-2">
              <div className="h-10 bg-gray-200 rounded animate-pulse" />
              <div className="h-6 bg-gray-200 rounded w-1/2 ml-4 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('w-80 bg-gray-50 border-r border-gray-200 h-full flex flex-col', className)}>
      <div className="px-4 py-4 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900">{t('learning.reviewSidebar.contentReview')}</h2>
        <p className="text-xs text-gray-500 mt-0.5">{t('learning.reviewSidebar.contentCount', { count: contentEntries.length })}</p>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {contentEntries.map((entry, index) => {
          const query = hierarchyQueries[index]
          const hierarchy = query?.data
          const isLoading = query?.isLoading

          return (
            <div key={entry.contentId} className="border-b border-gray-100 last:border-b-0">
              {/* Section header - accordion toggle */}
              <button
                onClick={() => toggleSection(entry.contentId)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors bg-gray-50 hover:bg-gray-100',
                )}
              >
                <div className="flex-shrink-0 text-purple-600">
                  <Layers className="w-4 h-4" />
                </div>
                <span className="flex-1 text-sm font-bold text-gray-900 truncate">
                  {entry.label}
                </span>
                <div className="flex-shrink-0 text-gray-400">
                  {isSectionOpen(entry.contentId) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
              </button>

              {/* Section content */}
              {isSectionOpen(entry.contentId) && (
                <div className="bg-white">
                  {isLoading ? (
                    <div className="px-4 py-3 space-y-2">
                      <div className="h-6 bg-gray-200 rounded animate-pulse" />
                      <div className="h-6 bg-gray-200 rounded w-3/4 animate-pulse" />
                    </div>
                  ) : hierarchy ? (
                    renderHierarchy(hierarchy, entry.contentId)
                  ) : (
                    <p className="px-4 py-2 text-xs text-gray-400">{t('learning.reviewSidebar.loadFailed')}</p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
